import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v7";
import { Navigate } from "react-router-dom";

import { Authorize } from "@components/Authorize/Authorize";
import { QA_RULES_ROLES } from "@pages/QARules/constants";

import { AuthGuard } from "./AuthGuard";
import { DefaultRedirect } from "./DefaultRedirect";
import { AppShell } from "./layouts/AppShell";
import { EvalRubrics } from "./pages/EvalRubric/EvalRubrics";
import { EvalResults } from "./pages/Evaluations/EvalResults";
import { LearnerTestProfileEditor, LearnerTestProfileEditorKeyed } from "./pages/Evaluations/LearnerTestProfileEditor";
import { LearnerTestProfilesList } from "./pages/Evaluations/LearnerTestProfilesList";
import { ModelTesting, ModelTestingComparison, ModelTestingMultiTest } from "./pages/Evaluations/ModelTesting";
import { ExpertChatDetailPage } from "./pages/ExpertChats/ExpertChatDetailPage";
import { ExpertChatsPage } from "./pages/ExpertChats/ExpertChatsPage";
import { ExportTesting } from "./pages/ExportTesting/ExportTesting";
import { CommunityDigest } from "./pages/Notifications/CommunityDigest";
import { AddClinicFlow } from "./pages/Organizations/AddClinicFlow";
import { ClinicDetailPage } from "./pages/Organizations/ClinicDetailPage";
import { EditClinicPage } from "./pages/Organizations/EditClinicPage";
import { OrganizationDetail } from "./pages/Organizations/OrganizationDetail";
import { OrganizationNew } from "./pages/Organizations/OrganizationNew";
import { Organizations } from "./pages/Organizations/Organizations";
import { UserDetail } from "./pages/Organizations/UserDetail";
import { NewPrompt } from "./pages/Prompts/NewPrompt";
import { PromptDetail } from "./pages/Prompts/PromptDetail";
import { PromptReviewDraft } from "./pages/Prompts/PromptReviewDraft";
import { PromptTestBed } from "./pages/Prompts/PromptTestBed";
import { Prompts } from "./pages/Prompts/Prompts";
import { QAPackageDetail } from "./pages/QARules/QAPackageDetail";
import { QARules } from "./pages/QARules/QARules";
import { BaseCriteriaSettings } from "./pages/QASettings/BaseCriteriaSettings";
import { GenerateTemplate } from "./pages/Templates/GenerateTemplate";
import { TemplateDetail } from "./pages/Templates/TemplateDetail";
import { Templates } from "./pages/Templates/Templates";
import { VerificationRequestDetailPage } from "./pages/VerificationRequests/VerificationRequestDetailPage";
import { VerificationRequestsPage } from "./pages/VerificationRequests/VerificationRequestsPage";
import { BasecampRole } from "./utils/types";

const CHAT_ADMIN_ROLES = [BasecampRole.ADMIN, BasecampRole.CHAT_ADMIN];
const ENABLEMENT_ROLES = [BasecampRole.ADMIN, BasecampRole.ENABLEMENT_ADMIN];
const ENTERPRISE_ROLES = [BasecampRole.ADMIN, BasecampRole.ENTERPRISE_ADMIN];
const ENABLEMENT_OR_ENTERPRISE_ROLES = [
  BasecampRole.ADMIN,
  BasecampRole.ENABLEMENT_ADMIN,
  BasecampRole.ENTERPRISE_ADMIN,
];
const NOTIFICATIONS_ROLES = [BasecampRole.ADMIN, BasecampRole.NOTIFICATIONS_ADMIN];

export const router = createBrowserRouter([
  {
    element: <AuthGuard />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <DefaultRedirect /> },
          {
            path: "organizations",
            children: [
              {
                index: true,
                element: (
                  <Authorize allowedRoles={ENABLEMENT_OR_ENTERPRISE_ROLES}>
                    <Organizations />
                  </Authorize>
                ),
              },
              {
                path: "new",
                element: (
                  <Authorize allowedRoles={ENABLEMENT_ROLES}>
                    <OrganizationNew />
                  </Authorize>
                ),
              },
              {
                path: ":orgId",
                children: [
                  {
                    index: true,
                    element: (
                      <Authorize allowedRoles={ENABLEMENT_OR_ENTERPRISE_ROLES}>
                        <OrganizationDetail />
                      </Authorize>
                    ),
                  },
                  {
                    path: "users/:userId",
                    element: (
                      <Authorize allowedRoles={ENABLEMENT_OR_ENTERPRISE_ROLES}>
                        <UserDetail />
                      </Authorize>
                    ),
                  },
                  {
                    path: "clinics",
                    children: [
                      {
                        path: "add",
                        element: (
                          <Authorize allowedRoles={ENABLEMENT_ROLES}>
                            <AddClinicFlow />
                          </Authorize>
                        ),
                      },
                      {
                        path: ":clinicId",
                        children: [
                          {
                            index: true,
                            element: (
                              <Authorize allowedRoles={ENABLEMENT_ROLES}>
                                <ClinicDetailPage />
                              </Authorize>
                            ),
                          },
                          {
                            path: "edit",
                            element: (
                              <Authorize allowedRoles={ENABLEMENT_ROLES}>
                                <EditClinicPage />
                              </Authorize>
                            ),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "expert-chats",
            children: [
              {
                index: true,
                element: (
                  <Authorize allowedRoles={CHAT_ADMIN_ROLES}>
                    <ExpertChatsPage />
                  </Authorize>
                ),
              },
              {
                path: ":chatId",
                element: (
                  <Authorize allowedRoles={CHAT_ADMIN_ROLES}>
                    <ExpertChatDetailPage />
                  </Authorize>
                ),
              },
            ],
          },
          {
            path: "verification-requests",
            children: [
              {
                index: true,
                element: (
                  <Authorize allowedRoles={ENABLEMENT_ROLES}>
                    <VerificationRequestsPage />
                  </Authorize>
                ),
              },
              {
                path: ":clinicListingId",
                element: (
                  <Authorize allowedRoles={ENABLEMENT_ROLES}>
                    <VerificationRequestDetailPage />
                  </Authorize>
                ),
              },
            ],
          },
          {
            path: "eval-results",
            element: (
              <Authorize allowedRoles={ENTERPRISE_ROLES}>
                <EvalResults />
              </Authorize>
            ),
          },
          {
            path: "model-testing",
            element: (
              <Authorize allowedRoles={ENTERPRISE_ROLES}>
                <ModelTesting />
              </Authorize>
            ),
          },
          {
            path: "model-testing/multi/:testId",
            element: (
              <Authorize allowedRoles={ENTERPRISE_ROLES}>
                <ModelTestingMultiTest />
              </Authorize>
            ),
          },
          {
            path: "model-testing/comparisons/:comparisonId",
            element: (
              <Authorize allowedRoles={ENTERPRISE_ROLES}>
                <ModelTestingComparison />
              </Authorize>
            ),
          },
          {
            path: "eval-rubrics",
            element: (
              <Authorize allowedRoles={ENTERPRISE_ROLES}>
                <EvalRubrics />
              </Authorize>
            ),
          },
          {
            path: "prompts",
            children: [
              {
                index: true,
                element: (
                  <Authorize allowedRoles={ENTERPRISE_ROLES}>
                    <Prompts />
                  </Authorize>
                ),
              },
              {
                // Reserved path — must come before ``:key`` so
                // ``/prompts/new`` resolves to the setup page instead
                // of trying to load a Prompt with key ``"new"``.
                path: "new",
                element: (
                  <Authorize allowedRoles={ENTERPRISE_ROLES}>
                    <NewPrompt />
                  </Authorize>
                ),
              },
              {
                path: ":key",
                element: (
                  <Authorize allowedRoles={ENTERPRISE_ROLES}>
                    <PromptDetail />
                  </Authorize>
                ),
              },
              {
                path: ":key/evaluations",
                element: (
                  <Authorize allowedRoles={ENTERPRISE_ROLES}>
                    <PromptTestBed />
                  </Authorize>
                ),
              },
              {
                path: ":key/review-draft",
                element: (
                  <Authorize allowedRoles={ENTERPRISE_ROLES}>
                    <PromptReviewDraft />
                  </Authorize>
                ),
              },
            ],
          },
          {
            path: "evaluations",
            children: [
              {
                index: true,
                // Landing on /evaluations bounces to the profiles list —
                // that's the only Evaluations config surface today. When
                // we add more (runs history, imports), this becomes a
                // hub page.
                element: (
                  <Authorize allowedRoles={ENTERPRISE_ROLES}>
                    <LearnerTestProfilesList />
                  </Authorize>
                ),
              },
              {
                path: "profiles",
                element: (
                  <Authorize allowedRoles={ENTERPRISE_ROLES}>
                    <LearnerTestProfilesList />
                  </Authorize>
                ),
              },
              {
                // `key` forces a fresh mount when navigating between
                // /profiles/new and /profiles/:id (or between different
                // :id's), which wipes local state so a fresh create
                // doesn't inherit the prior profile's form.
                path: "profiles/new",
                element: (
                  <Authorize allowedRoles={ENTERPRISE_ROLES}>
                    <LearnerTestProfileEditor key="new" />
                  </Authorize>
                ),
              },
              {
                path: "profiles/:id",
                element: (
                  <Authorize allowedRoles={ENTERPRISE_ROLES}>
                    <LearnerTestProfileEditorKeyed />
                  </Authorize>
                ),
              },
            ],
          },
          {
            path: "templates",
            children: [
              {
                index: true,
                element: (
                  <Authorize allowedRoles={ENTERPRISE_ROLES}>
                    <Templates />
                  </Authorize>
                ),
              },
              {
                path: "generate",
                element: (
                  <Authorize allowedRoles={ENTERPRISE_ROLES}>
                    <GenerateTemplate />
                  </Authorize>
                ),
              },
              {
                path: ":templateId",
                element: (
                  <Authorize allowedRoles={ENTERPRISE_ROLES}>
                    <TemplateDetail />
                  </Authorize>
                ),
              },
            ],
          },
          {
            path: "base-criteria",
            element: (
              <Authorize allowedRoles={ENTERPRISE_ROLES}>
                <BaseCriteriaSettings />
              </Authorize>
            ),
          },
          {
            path: "qa-rules",
            element: (
              <Authorize allowedRoles={QA_RULES_ROLES}>
                <QARules />
              </Authorize>
            ),
          },
          // The QA package (v2) manager lived here; send old bookmarks to its replacement.
          { path: "qa-packages", element: <Navigate to="/qa-rules" replace /> },
          {
            path: "qa-rules/packages/:packageId",
            element: (
              <Authorize allowedRoles={QA_RULES_ROLES}>
                <QAPackageDetail />
              </Authorize>
            ),
          },
          {
            path: "export-testing",
            element: (
              <Authorize allowedRoles={ENABLEMENT_OR_ENTERPRISE_ROLES}>
                <ExportTesting />
              </Authorize>
            ),
          },
          {
            path: "notifications",
            children: [
              { index: true, element: <Navigate to="/notifications/community-digest" replace /> },
              {
                path: "community-digest",
                element: (
                  <Authorize allowedRoles={NOTIFICATIONS_ROLES}>
                    <CommunityDigest />
                  </Authorize>
                ),
              },
            ],
          },
          { path: "*", element: <DefaultRedirect /> },
        ],
      },
    ],
  },
]);
